import { type EventCode, LATEST_DATA } from 'api-validations/stitch'

import { defaultState, type MapState } from '../core'
import {
  asAppliedLayer,
  boundaryCreatorLayer,
  prescriptionCreatorLayer,
  defaultPartition,
  fieldLayer,
  groupContaining,
  isEditorLayer,
  type LayerSelection,
  moveStream,
  partitionKeyOf,
  planAssetEditorLayer,
  planEditorLayer,
  planningLayer,
  primaryOf,
  removeStream,
  SatelliteImagery,
  satelliteLayer,
  soilSampleLayer,
  soilSurveyLayer,
  type StreamPartition,
  type TerrainLayer
} from '../data-layer-state'
import {
  type BrowseFields,
  defaultBrowseFields,
  type FilterState,
  type InField,
  type PaneScope
} from '../session'
import {
  Pane,
  type PaneTarget,
  type RequestedMapAction,
  type Resolution,
  type ResolvedMapAction,
  UnresolvableReason
} from './actions'
import type { BrowseAddress, LayerAddress, MapAddress } from './address'
import type { AuthoredStream, CatalogStream, Cff, MapCatalog } from './catalog'
import { EquivalencyOutcome, type LayerEquivalencyKey } from './layer-scope'
import {
  equivalencyKeyFor,
  type ResolvedLayer,
  type ResolvedStreamLayer,
  resolveStreamLayer
} from './resolve-layer'
import {
  cffOf,
  lastModifiedRecency,
  pickStream,
  type StreamPreference,
  streamsInField
} from './resolve-stream'

/**
 * Everything a request needs looked up, done. The result is either a patch the
 * reducer can commit unconditionally, a request that asked for what is already
 * true, or a reason nothing can be committed at all.
 *
 * Only the catalog is consulted for what exists; whether the resulting state is
 * renderable is not knowable here and stays the renderer's problem.
 */
export const resolve = async (
  prev: MapState,
  action: RequestedMapAction,
  catalog: MapCatalog
): Promise<Resolution> => {
  switch (action.kind) {
    case 'EnterField':
      return enterField(action.cff, prev, catalog)

    case 'EnterStream':
      return enterStream(action.streamId, catalog)

    case 'NavigateToAddress':
      return navigateToAddress(action.address, catalog)

    case 'ChangeField':
      return changeField(action.cff, prev, catalog)

    case 'SelectCropCycle': {
      const current = paneSelection(prev, action.pane)
      const cff = await fieldOf(current, catalog)
      if (cff === null) return unresolvable(UnresolvableReason.NoStreamInCropCycle)

      // A plan asset and a terrain layer belong to the field, not to a crop
      // cycle, so moving the year moves what the pickers list without moving
      // what the pane draws.
      const stream = await streamFor(current, catalog)
      if (current !== null && current.kind !== 'AsApplied') {
        return commit(
          {
            kind: 'SetPaneScope',
            pane: action.pane,
            scope: { season: action.year, cropKey: action.cropKey }
          },
          EquivalencyOutcome.NotApplicable
        )
      }

      return movePane({
        pane: action.pane,
        current,
        candidates: streamsInField(await catalog.allStreams(), cff).filter(
          (s) => s.season === action.year && s.cropKey === action.cropKey
        ),
        prefer: { task: stream?.task, serialNumber: stream?.serialNumber },
        absent: UnresolvableReason.NoStreamInCropCycle,
        catalog
      })
    }

    case 'SelectTask': {
      const current = paneSelection(prev, action.pane)
      const stream = await streamFor(current, catalog)
      if (stream === undefined) return unresolvable(UnresolvableReason.NoStreamForTask)
      return movePane({
        pane: action.pane,
        current,
        candidates: streamsInField(await catalog.allStreams(), cffOf(stream)).filter(
          (s) =>
            s.task === action.task && s.season === stream.season && s.cropKey === stream.cropKey
        ),
        prefer: { serialNumber: stream.serialNumber },
        absent: UnresolvableReason.NoStreamForTask,
        catalog
      })
    }

    case 'SelectStreamGroup': {
      // The group's primary carries the selection; the rest ride along as the merge.
      const primary = primaryOf(action.group)
      if (primary === null) return noOp
      return selectStream({
        pane: action.pane,
        streamId: primary,
        mergedStreamIds: action.group.streamIds.filter((id) => id !== primary),
        current: paneSelection(prev, action.pane),
        catalog
      })
    }

    case 'AddStreamToGroup':
    case 'RemoveStreamFromGroup':
      return regroupStreams(action, prev, catalog)

    case 'SelectLayer':
      return commit(
        {
          kind: 'SetPaneLayer',
          pane: action.pane,
          layer: action.layer,
          scope: await scopeAfter({ layer: action.layer, prev, pane: action.pane, catalog })
        },
        EquivalencyOutcome.NotApplicable
      )

    case 'EnterPlanEditor': {
      const layer = planEditorLayer(action.planningFieldId, action.planId)
      return commit(
        {
          kind: 'EnterEditor',
          layer,
          scope: await scopeAfter({ layer, prev, pane: Pane.Main, catalog })
        },
        EquivalencyOutcome.NotApplicable
      )
    }

    case 'EnterPlanAssetEditor': {
      const layer = planAssetEditorLayer(action.planningFieldId, action.planId, action.asset)
      return commit(
        {
          kind: 'EnterEditor',
          layer,
          scope: await scopeAfter({ layer, prev, pane: Pane.Main, catalog })
        },
        EquivalencyOutcome.NotApplicable
      )
    }

    case 'EnterBoundaryCreator':
    case 'EnterPrescriptionCreator': {
      const layer =
        action.kind === 'EnterBoundaryCreator'
          ? boundaryCreatorLayer(action.planningFieldId)
          : prescriptionCreatorLayer(action.planningFieldId)
      return commit(
        {
          kind: 'EnterEditor',
          layer,
          scope: await scopeAfter({ layer, prev, pane: Pane.Main, catalog })
        },
        EquivalencyOutcome.NotApplicable
      )
    }

    case 'ExitEditor':
      return exitEditor(prev, catalog)

    case 'EnterSplit': {
      // A pane that was only hidden still holds the layer the user left in it.
      if (prev.session.kind === 'InField' && prev.session.comparisonMap !== null) {
        return commit(
          { kind: 'SetComparisonDisabled', disabled: false },
          EquivalencyOutcome.NotApplicable
        )
      }
      const main = paneSelection(prev, Pane.Main)
      // The comparison pane cannot hold an editor, so there is nothing to
      // copy into it while one is open.
      if (main === null || isEditorLayer(main)) return noOp
      // The new pane starts as a copy of the one it was split from, scope included.
      return commit(
        {
          kind: 'SetPaneLayer',
          pane: Pane.Comparison,
          layer: main,
          scope: prev.paneScopes.main
        },
        EquivalencyOutcome.NotApplicable
      )
    }

    case 'ExitSplit':
      return commit(
        { kind: 'SetComparisonDisabled', disabled: true },
        EquivalencyOutcome.NotApplicable
      )

    case 'SetBrowseFilter':
      return setBrowseFilter(action.filter, prev, catalog)

    case 'ReturnToBrowseFields':
      // Already there, and committing would drop the filter being browsed under.
      if (prev.session.kind === 'BrowseFields') return noOp
      return commit(
        { kind: 'ReturnToBrowseFields', session: defaultBrowseFields },
        EquivalencyOutcome.NotApplicable
      )

    case 'SetMode':
    case 'SetDetailsTab':
      // Mode and its tabs are in-field furniture; the field list offers neither.
      if (prev.session.kind !== 'InField') return noOp
      return commit(action, EquivalencyOutcome.NotApplicable)

    case 'SetRecordWithHistory':
    case 'SetRecordWithoutHistory':
    case 'GoBackRecord':
    case 'GoForwardRecord':
      // Nothing outside prior state to look up, so the request is already the patch.
      return commit(action, EquivalencyOutcome.NotApplicable)

    case 'PickRecordAtPoint': {
      const current = paneSelection(prev, Pane.Main)
      if (current?.kind !== 'AsApplied') return noOp
      const record = await catalog.recordAt({
        streamId: current.streamId,
        coordinate: action.coordinate
      })
      return commit({ kind: 'SetRecordWithHistory', record }, EquivalencyOutcome.NotApplicable)
    }

    case 'ViewDiagnoseTable':
      return viewDiagnoseTable(action.code, prev, catalog)
  }
}

/**
 * A filter means nothing to a pane, so the only question is whether the field
 * the panes are in survives it. One that does leaves nothing to write; one that
 * does not makes the field list the only place the request can be honored.
 */
const setBrowseFilter = async (
  filter: FilterState,
  prev: MapState,
  catalog: MapCatalog
): Promise<Resolution> => {
  if (prev.session.kind === 'BrowseFields') {
    return sameFilter(prev.session.filter, filter)
      ? noOp
      : commit({ kind: 'SetBrowseFilter', filter }, EquivalencyOutcome.NotApplicable)
  }

  const cff = await fieldOf(prev.session.mainMap, catalog)
  if (cff !== null && fieldSatisfiesFilter(cff, filter)) return noOp
  return commit(
    { kind: 'ReturnToBrowseFields', session: { ...defaultBrowseFields, filter } },
    EquivalencyOutcome.NotApplicable
  )
}

const fieldSatisfiesFilter = (cff: Cff, filter: FilterState): boolean => {
  if (filter.level === 'None') return true
  if (filter.client !== cff.client) return false
  return filter.level === 'Client' || filter.farm === cff.farm
}

const sameFilter = (a: FilterState, b: FilterState): boolean =>
  a.level === b.level && a.client === b.client && a.farm === b.farm

// ---------------------------------------------------------------------------
// Entering
// ---------------------------------------------------------------------------

/**
 * A field named by name. The user chose no pass, so the only preference to
 * honor is the browse list's own season filter — landing outside the season
 * they were just filtering by is the move reading as a bug.
 */
const enterField = async (cff: Cff, prev: MapState, catalog: MapCatalog): Promise<Resolution> => {
  const seasonFilter = prev.session.kind === 'BrowseFields' ? prev.session.seasonFilter : null
  const resolved = await resolveBestStream({
    candidates: streamsInField(await catalog.allStreams(), cff),
    prefer:
      seasonFilter === null ? {} : { season: seasonFilter.year, cropKey: seasonFilter.cropKey },
    key: null,
    catalog
  })
  if (resolved === null) return enterFieldItself(cff, catalog)
  return layerCommit(Pane.Main, resolved)
}

/**
 * A field with no as-applied data is still somewhere to be: the field itself,
 * with its plans and terrain still listed by the pickers. Only a field planning
 * has never registered has neither, and so nothing to enter.
 */
const enterFieldItself = async (cff: Cff, catalog: MapCatalog): Promise<Resolution> => {
  const planningFieldId = await catalog.planningFieldFor(cff)
  if (planningFieldId === null) return unresolvable(UnresolvableReason.FieldHasNoStreams)
  return commit(
    { kind: 'SetPaneLayer', pane: Pane.Main, layer: fieldLayer(planningFieldId), scope: null },
    EquivalencyOutcome.NotApplicable
  )
}

const enterStream = async (streamId: string, catalog: MapCatalog): Promise<Resolution> => {
  if ((await catalog.streamById(streamId)) === undefined) {
    return unresolvable(UnresolvableReason.StreamNotFound)
  }
  return layerCommit(Pane.Main, await resolveStreamLayer({ streamId, key: null, catalog }))
}

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

/**
 * An address is total: it is resolved without reference to prior state, so the
 * same link reaches the same place whether the page is loading cold or the user
 * is already somewhere else.
 */
const navigateToAddress = async (address: MapAddress, catalog: MapCatalog): Promise<Resolution> => {
  if (address.kind === 'Browse') {
    return commit(
      { kind: 'ReturnToBrowseFields', session: browseSessionFrom(address) },
      EquivalencyOutcome.NotApplicable
    )
  }

  const main = await resolveAddress(address.main, catalog)
  if (main.kind === 'Unresolvable') return unresolvable(main.reason)

  const comparison =
    address.comparison === undefined
      ? null
      : await resolveAddress(address.comparison.layer, catalog)

  const session: InField = {
    kind: 'InField',
    mainMap: main.layer,
    comparisonMap:
      comparison?.kind !== 'Found' || address.comparison === undefined
        ? null
        : { selection: comparison.layer, disabled: address.comparison.hidden }
  }

  return commit(
    {
      kind: 'RestoreSession',
      session,
      record: address.record ?? LATEST_DATA,
      mode: address.mode ?? defaultState.mode,
      activeNoteId: address.activeNoteId ?? null
    },
    EquivalencyOutcome.NotApplicable
  )
}

type AddressResolution =
  | { kind: 'Found'; layer: LayerSelection }
  | { kind: 'Unresolvable'; reason: UnresolvableReason }

const found = (layer: LayerSelection): AddressResolution => ({ kind: 'Found', layer })

const notFound = (reason: UnresolvableReason): AddressResolution => ({
  kind: 'Unresolvable',
  reason
})

/** One pane's address as a selection, closing whatever the address left open. */
const resolveAddress = async (
  address: LayerAddress,
  catalog: MapCatalog
): Promise<AddressResolution> => {
  switch (address.kind) {
    case 'Cff': {
      const resolved = await resolveBestStream({
        candidates: streamsInField(await catalog.allStreams(), address.cff),
        prefer: {},
        key: null,
        catalog
      })
      if (resolved !== null) return found(resolved.layer)
      const planningFieldId = await catalog.planningFieldFor(address.cff)
      return planningFieldId === null
        ? notFound(UnresolvableReason.FieldHasNoStreams)
        : found(fieldLayer(planningFieldId))
    }

    case 'Field':
      return found(fieldLayer(address.planningFieldId))

    case 'Stream': {
      if ((await catalog.streamById(address.streamId)) === undefined) {
        return notFound(UnresolvableReason.StreamNotFound)
      }
      if (address.layer !== undefined) {
        return found(
          asAppliedLayer({
            streamId: address.streamId,
            applicationId: address.layer.applicationId,
            layerId: address.layer.layerId,
            mergedStreamIds: [...(address.mergedStreamIds ?? [])]
          })
        )
      }
      const resolved = await resolveStreamLayer({
        streamId: address.streamId,
        key: null,
        catalog
      })
      if (resolved === null) return notFound(UnresolvableReason.ScopeHasNoLayers)
      return found({ ...resolved.layer, mergedStreamIds: [...(address.mergedStreamIds ?? [])] })
    }

    case 'PlanAsset': {
      // A guidance composite is identified by its plan rather than by an asset,
      // so there is nothing to look its plan up by: unnamed means unassigned.
      if (address.asset.assetType === 'GuidancePlan') {
        return found(planningLayer.guidancePlan(address.planningFieldId, address.planId ?? null))
      }
      const planId =
        address.planId === undefined
          ? await catalog.planFor({
              planningFieldId: address.planningFieldId,
              assetType: address.asset.assetType,
              assetId: address.asset.assetId
            })
          : address.planId
      return found({
        kind: 'Planning',
        planningFieldId: address.planningFieldId,
        planId,
        ...address.asset
      })
    }

    case 'PlanEditor':
      return found(planEditorLayer(address.planningFieldId, address.planId))

    case 'BoundaryCreator':
      return found(boundaryCreatorLayer(address.planningFieldId))

    case 'PrescriptionCreator':
      return found(prescriptionCreatorLayer(address.planningFieldId))

    case 'Satellite':
      return found(
        satelliteLayer({
          planningFieldId: address.planningFieldId,
          imagery: address.imagery ?? defaultImagery,
          captureDate: address.captureDate ?? null
        })
      )

    case 'SoilSample':
      return found(
        soilSampleLayer({
          planningFieldId: address.planningFieldId,
          samplingEventId: address.samplingEventId,
          measurement: address.measurement
        })
      )

    case 'SoilSurvey':
      return found(
        soilSurveyLayer({ planningFieldId: address.planningFieldId, attribute: address.attribute })
      )
  }
}

// ---------------------------------------------------------------------------
// Moving
// ---------------------------------------------------------------------------

/**
 * A field change moves the page, so both panes land somewhere in the new field
 * — each from its own starting point, since what a pane was showing is the only
 * guide to what it should show next.
 *
 * A pane on a plan asset or a soil sample has no counterpart in another field
 * (one field's boundary is not another's), so it lands on that field's data
 * instead — or on the field itself, where there is no data to land on.
 * Satellite imagery and the soil survey survive, being products rather than
 * objects.
 */
const changeField = async (cff: Cff, prev: MapState, catalog: MapCatalog): Promise<Resolution> => {
  const candidates = streamsInField(await catalog.allStreams(), cff)
  const main = await landInField({
    current: paneSelection(prev, Pane.Main),
    candidates,
    cff,
    catalog
  })
  if (main === null) return unresolvable(UnresolvableReason.FieldHasNoStreams)

  // A pane arriving in a field has no crop cycle to bring with it: the one it
  // held belonged to the field it just left.
  const comparisonBefore = paneSelection(prev, Pane.Comparison)
  if (comparisonBefore === null) {
    return commit(
      { kind: 'SetPaneLayer', pane: Pane.Main, layer: main.layer, scope: null },
      main.outcome
    )
  }

  const comparison = await landInField({
    current: comparisonBefore,
    candidates,
    cff,
    catalog
  })
  return commit(
    {
      kind: 'SetBothPanes',
      main: { layer: main.layer, scope: null },
      comparison: { layer: comparison?.layer ?? main.layer, scope: null }
    },
    main.outcome
  )
}

const landInField = async ({
  current,
  candidates,
  cff,
  catalog
}: {
  current: LayerSelection | null
  candidates: readonly CatalogStream[]
  cff: Cff
  catalog: MapCatalog
}): Promise<ResolvedLayer | null> => {
  const product = current === null ? null : await productInField({ current, cff, catalog })
  if (product !== null) return { layer: product, outcome: EquivalencyOutcome.Exact }

  const stream = await streamFor(current, catalog)
  const key = current === null ? null : await equivalencyKeyFor(current, catalog)
  const resolved = await resolveBestStream({
    candidates,
    prefer: stream === undefined ? {} : fullPreference(stream),
    key,
    catalog
  })
  if (resolved === null) {
    const planningFieldId = await catalog.planningFieldFor(cff)
    return planningFieldId === null
      ? null
      : { layer: fieldLayer(planningFieldId), outcome: EquivalencyOutcome.Substituted }
  }

  // A pane arriving from outside the stream branch was substituted, however
  // well its layer then matched.
  return current === null || current.kind === 'AsApplied'
    ? resolved
    : { ...resolved, outcome: EquivalencyOutcome.Substituted }
}

const movePane = async ({
  pane,
  current,
  candidates,
  prefer,
  absent,
  catalog
}: {
  pane: Pane
  current: LayerSelection | null
  candidates: readonly CatalogStream[]
  prefer: StreamPreference
  absent: UnresolvableReason
  catalog: MapCatalog
}): Promise<Resolution> => {
  if (candidates.length === 0) return unresolvable(absent)
  const key = current === null ? null : await equivalencyKeyFor(current, catalog)
  return layerCommit(pane, await resolveBestStream({ candidates, prefer, key, catalog }))
}

/**
 * The preferred stream among `candidates` that carries something renderable.
 * A stream with no layers cannot be shown, so however well it matches, the
 * pick falls through it to the next-best rather than failing the whole move.
 * `null` when no candidate is renderable.
 */
const resolveBestStream = async ({
  candidates,
  prefer,
  key,
  catalog
}: {
  candidates: readonly CatalogStream[]
  prefer: StreamPreference
  key: LayerEquivalencyKey | null
  catalog: MapCatalog
}): Promise<ResolvedStreamLayer | null> => {
  let remaining = candidates
  while (remaining.length > 0) {
    const picked = pickStream({ candidates: remaining, prefer, recencyOf: lastModifiedRecency })
    if (picked === null) return null
    const resolved = await resolveStreamLayer({ streamId: picked.streamId, key, catalog })
    if (resolved !== null) return resolved
    remaining = remaining.filter((s) => s.streamId !== picked.streamId)
  }
  return null
}

/**
 * A diagnose table belongs to one of the code's modules, and its application
 * must be the one on screen before the table can render. Prefer the module
 * whose application the pane already shows; otherwise move to the first
 * module's application, landing on its first layer — keeping the current layer
 * id when that application offers none.
 */
const viewDiagnoseTable = async (
  code: EventCode,
  prev: MapState,
  catalog: MapCatalog
): Promise<Resolution> => {
  const current = paneSelection(prev, Pane.Main)
  if (current?.kind !== 'AsApplied') return noOp
  const module =
    code.modules.find((m) => m.application_id === current.applicationId) ?? code.modules.at(0)
  if (module === undefined) return noOp

  let layer = current
  if (module.application_id !== current.applicationId) {
    const applications = await catalog.applicationsFor(current.streamId)
    const target = applications.find((a) => a.application_id === module.application_id)
    layer = {
      ...current,
      applicationId: module.application_id,
      layerId: target?.layers.at(0)?.id ?? current.layerId
    }
  }

  return commit(
    {
      kind: 'ViewDiagnoseTable',
      layer,
      // A pass names its own crop cycle and task.
      scope: null,
      record: code.start_record,
      focusedModules: code.modules.map((m) => m.key)
    },
    EquivalencyOutcome.NotApplicable
  )
}

const selectStream = async ({
  pane,
  streamId,
  mergedStreamIds,
  current,
  catalog
}: {
  pane: Pane
  streamId: string
  mergedStreamIds: readonly string[]
  current: LayerSelection | null
  catalog: MapCatalog
}): Promise<Resolution> => {
  if ((await catalog.streamById(streamId)) === undefined) {
    return unresolvable(UnresolvableReason.StreamNotFound)
  }
  const key = current === null ? null : await equivalencyKeyFor(current, catalog)
  const resolved = await resolveStreamLayer({ streamId, key, catalog })
  if (resolved === null) return unresolvable(UnresolvableReason.ScopeHasNoLayers)
  return commit(
    {
      kind: 'SetPaneLayer',
      pane,
      layer: { ...resolved.layer, mergedStreamIds: [...mergedStreamIds] },
      // A pass names its own crop cycle and task.
      scope: null
    },
    resolved.outcome
  )
}

/**
 * Regrouping edits the partition the picker is showing, which is the pane's own
 * if it has customized one and the same-day default otherwise. Both edits are
 * pure transforms over that partition; what needs resolving is where they leave
 * the pane, since the group it sits in may come out with a different primary.
 */
const regroupStreams = async (
  action:
    | { kind: 'AddStreamToGroup'; pane: Pane; streamId: string; groupMember: string }
    | { kind: 'RemoveStreamFromGroup'; pane: Pane; streamId: string },
  prev: MapState,
  catalog: MapCatalog
): Promise<Resolution> => {
  const edited = await catalog.streamById(action.streamId)
  if (edited === undefined) return unresolvable(UnresolvableReason.StreamNotFound)

  const groupable = await catalog.streamsGroupableWith(action.streamId)
  const authoredAt = new Map(groupable.map((s) => [s.streamId, s.authoredAt.getTime()]))
  const rankOf = (id: string) => authoredAt.get(id) ?? 0

  const current = paneSelection(prev, action.pane)
  const scopeKey = partitionKeyOf(edited)
  const partition = displayedPartition({ prev, pane: action.pane, scopeKey, groupable, rankOf })

  const next =
    action.kind === 'AddStreamToGroup'
      ? moveStream(partition, action.streamId, action.groupMember, rankOf)
      : removeStream(partition, action.streamId, rankOf)
  // Both transforms return their input when the edit would change nothing —
  // moving a stream into the group it is already in, emptying a sole member.
  if (next === partition) return noOp

  const target = await paneAfterRegrouping({
    partition: next,
    current,
    followed: followedStream({ partition, current, action }),
    catalog
  })
  if (target === null && current?.kind === 'AsApplied') {
    return unresolvable(UnresolvableReason.ScopeHasNoLayers)
  }
  return commit(
    { kind: 'SetStreamGrouping', pane: action.pane, scopeKey, partition: next, target },
    EquivalencyOutcome.NotApplicable
  )
}

/**
 * The partition the user is editing. A stored one is authoritative; without one
 * the picker starts from the same-day default and folds in whatever merge the
 * pane's selection already carries, so a group restored from a link is editable
 * before it has been customized.
 */
const displayedPartition = ({
  prev,
  pane,
  scopeKey,
  groupable,
  rankOf
}: {
  prev: MapState
  pane: Pane
  scopeKey: string
  groupable: readonly AuthoredStream[]
  rankOf: (id: string) => number
}): StreamPartition => {
  const stored = prev.streamGroups[pane][scopeKey]
  if (stored !== undefined) return stored

  const selection = paneSelection(prev, pane)
  const base = defaultPartition([...groupable])
  if (selection?.kind !== 'AsApplied') return base
  return selection.mergedStreamIds.reduce(
    (acc, id) => moveStream(acc, id, selection.streamId, rankOf),
    base
  )
}

/**
 * Picks the stream the pane stays with after the user regroups. That is
 * normally the stream the pane was showing. When the user removes that stream
 * from its group, though, they want it off the map, so the pane stays with the
 * rest of the group.
 */
const followedStream = ({
  partition,
  current,
  action
}: {
  partition: StreamPartition
  current: LayerSelection | null
  action: { kind: 'AddStreamToGroup' | 'RemoveStreamFromGroup'; streamId: string }
}): string | null => {
  if (current?.kind !== 'AsApplied') return null
  const removesPaneStream =
    action.kind === 'RemoveStreamFromGroup' && action.streamId === current.streamId
  if (!removesPaneStream) return current.streamId
  const before = groupContaining(partition, current.streamId)
  return before?.streamIds.find((id) => id !== current.streamId) ?? current.streamId
}

/**
 * Where the pane lands once the partition changes: on the primary of whichever
 * group now holds the followed stream. `null` when nothing was on a stream
 * there, or when the new primary carries nothing renderable.
 */
const paneAfterRegrouping = async ({
  partition,
  current,
  followed,
  catalog
}: {
  partition: StreamPartition
  current: LayerSelection | null
  followed: string | null
  catalog: MapCatalog
}): Promise<PaneTarget | null> => {
  if (current?.kind !== 'AsApplied' || followed === null) return null
  const group = groupContaining(partition, followed)
  const primary = group === null ? null : primaryOf(group)
  if (group === null || primary === null) return null

  const resolved = await resolveStreamLayer({
    streamId: primary,
    key: await equivalencyKeyFor(current, catalog),
    catalog
  })
  if (resolved === null) return null
  return {
    layer: {
      ...resolved.layer,
      mergedStreamIds: group.streamIds.filter((id) => id !== primary)
    },
    // A pass names its own crop cycle and task.
    scope: null
  }
}

/**
 * Leaving an editor returns to whatever the pane held when it opened. A pane
 * that entered the editor from a deep link held nothing: an asset editor still
 * names the asset it was editing, so it lands there viewing it, while the plan
 * editor names only the field, so it lands on the field's most recent pass —
 * or on the field itself where there is no pass to land on.
 */
const exitEditor = async (prev: MapState, catalog: MapCatalog): Promise<Resolution> => {
  if (prev.planEditorReturnTo !== null) {
    return commit(
      {
        kind: 'SetPaneLayer',
        pane: Pane.Main,
        layer: prev.planEditorReturnTo,
        scope: await scopeAfter({ layer: prev.planEditorReturnTo, prev, pane: Pane.Main, catalog })
      },
      EquivalencyOutcome.NotApplicable
    )
  }

  const main = paneSelection(prev, Pane.Main)
  if (main === null || main.kind === 'AsApplied') return noOp

  if (main.kind === 'PlanAssetEditor') {
    const layer =
      main.assetType === 'GuidancePlan'
        ? planningLayer.guidancePlan(main.planningFieldId, main.planId)
        : main.assetType === 'Boundary'
          ? planningLayer.boundary(main.planningFieldId, main.planId, main.assetId)
          : planningLayer.prescription(main.planningFieldId, main.planId, main.assetId)
    return commit(
      {
        kind: 'SetPaneLayer',
        pane: Pane.Main,
        layer,
        scope: await scopeAfter({ layer, prev, pane: Pane.Main, catalog })
      },
      EquivalencyOutcome.NotApplicable
    )
  }

  const cff = await catalog.cffForPlanningField(main.planningFieldId)
  if (cff === null) return unresolvable(UnresolvableReason.FieldNotRegisteredForPlanning)

  const resolved = await resolveBestStream({
    candidates: streamsInField(await catalog.allStreams(), cff),
    prefer: {},
    key: null,
    catalog
  })
  if (resolved === null) {
    return commit(
      {
        kind: 'SetPaneLayer',
        pane: Pane.Main,
        layer: fieldLayer(main.planningFieldId),
        scope: null
      },
      EquivalencyOutcome.NotApplicable
    )
  }
  return layerCommit(Pane.Main, resolved)
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** The product the terrain picker lists first, for an address that named none. */
const defaultImagery = SatelliteImagery.Biomass

/**
 * The layer a product selection becomes in the field being entered, or `null`
 * for a selection that is not a product, or a field with no boundary to show
 * it on.
 */
const productInField = async ({
  current,
  cff,
  catalog
}: {
  current: LayerSelection
  cff: Cff
  catalog: MapCatalog
}): Promise<TerrainLayer | null> => {
  const inField = async (build: (planningFieldId: string) => TerrainLayer) => {
    const planningFieldId = await catalog.planningFieldFor(cff)
    if (planningFieldId === null || !(await catalog.hasDefaultBoundary(planningFieldId))) {
      return null
    }
    return build(planningFieldId)
  }

  switch (current.kind) {
    case 'Satellite':
      return inField((planningFieldId) =>
        satelliteLayer({ planningFieldId, imagery: current.imagery, captureDate: null })
      )
    case 'SoilSurvey':
      return inField((planningFieldId) =>
        soilSurveyLayer({ planningFieldId, attribute: current.attribute })
      )
    default:
      return null
  }
}

const paneSelection = (prev: MapState, pane: Pane): LayerSelection | null => {
  if (prev.session.kind !== 'InField') return null
  return pane === Pane.Main ? prev.session.mainMap : (prev.session.comparisonMap?.selection ?? null)
}

/** The stream a selection sits on, or undefined when it sits outside the stream branch. */
const streamFor = async (
  layer: LayerSelection | null,
  catalog: MapCatalog
): Promise<CatalogStream | undefined> =>
  layer?.kind === 'AsApplied' ? await catalog.streamById(layer.streamId) : undefined

/**
 * The scope a pane holds once `layer` is showing there. A pass names its own
 * crop cycle, so landing on one needs none; landing outside the stream branch
 * takes the crop cycle from the pass being left behind, and keeps the scope it
 * already had when there was no pass to take one from.
 */
const scopeAfter = async ({
  layer,
  prev,
  pane,
  catalog
}: {
  layer: LayerSelection
  prev: MapState
  pane: Pane
  catalog: MapCatalog
}): Promise<PaneScope | null> => {
  if (layer.kind === 'AsApplied') return null
  const leaving = await streamFor(paneSelection(prev, pane), catalog)
  if (leaving === undefined) return prev.paneScopes[pane]
  return { season: leaving.season, cropKey: leaving.cropKey }
}

/**
 * The field a selection sits in, whichever branch it is on — the two branches
 * carry different identifiers for the same field, so one of them needs
 * translating before the other's streams can be found.
 */
const fieldOf = async (layer: LayerSelection | null, catalog: MapCatalog): Promise<Cff | null> => {
  if (layer === null) return null
  if (layer.kind === 'AsApplied') {
    const stream = await streamFor(layer, catalog)
    return stream === undefined ? null : cffOf(stream)
  }
  return catalog.cffForPlanningField(layer.planningFieldId)
}

const fullPreference = (stream: CatalogStream): StreamPreference => ({
  season: stream.season,
  cropKey: stream.cropKey,
  task: stream.task,
  serialNumber: stream.serialNumber
})

const browseSessionFrom = (address: BrowseAddress): BrowseFields => ({
  ...defaultBrowseFields,
  filter: address.filter ?? defaultBrowseFields.filter,
  seasonFilter: address.seasonFilter ?? null,
  listView: address.listView ?? defaultBrowseFields.listView
})

const commit = (action: ResolvedMapAction, equivalency: EquivalencyOutcome): Resolution => ({
  kind: 'Commit',
  action,
  equivalency
})

const noOp: Resolution = { kind: 'NoOp' }

const unresolvable = (reason: UnresolvableReason): Resolution => ({ kind: 'Unresolvable', reason })

/** A stream that turned out to carry nothing renderable leaves nothing to commit. */
const layerCommit = (pane: Pane, resolved: ResolvedStreamLayer | null): Resolution =>
  resolved === null
    ? unresolvable(UnresolvableReason.ScopeHasNoLayers)
    : commit({ kind: 'SetPaneLayer', pane, layer: resolved.layer, scope: null }, resolved.outcome)
